import {
  FetchError,
  AuthenticationError,
  AuthorizationError,
  HttpError,
  NotFoundError,
  RateLimitError,
  ValidationError,
} from "./errors";

export interface ExtendedResponse<X> extends Response {
  data: X;
}

export interface ExtendedRequestInit<X> extends RequestInit {
  parser?: (response: Response) => X;
}

export default async function fetcher<X>(
  input: RequestInfo | URL,
  init?: ExtendedRequestInit<X>,
): Promise<ExtendedResponse<X>> {
  try {
    const response = await fetch(input, {
      ...createDefaultInit(),
      ...init,
      headers: {
        ...createDefaultHeaders(init?.headers),
      },
    });

    const parser = init?.parser;
    const data: X = parser ? parser(response) : await response.json();

    if (!response.ok) createFetchError("", response, data);

    const extendedResponse = createResponse(response, data);

    return extendedResponse;
  } catch (err) {
    if (err instanceof FetchError)
      return createResponse(err.response, err.data);
  }

  const fallbackResponse = new Response(null, {
    status: 500,
    statusText: "Internal Server Error",
  });
  return createResponse(fallbackResponse, null as X);
}
export function createFetchError<X>(
  message: string,
  response: Response,
  data: X,
): FetchError<X> {
  switch (response.status) {
    case 401:
      return new AuthenticationError(message, response, data);
    case 403:
      return new AuthorizationError(message, response, data);
    case 404:
      return new NotFoundError(message, response, data);
    case 429:
      return new RateLimitError(message, response, data);
    case 422:
      return new ValidationError(message, response, data);
    default:
      return new HttpError(message, response, data);
  }
}

function createDefaultHeaders(headers?: HeadersInit): HeadersInit {
  return {
    "X-Server-Auth": process.env.X_SERVER_AUTH ?? "",
    ...headers,
  };
}

function createDefaultInit(): RequestInit {
  return {
    cache: "no-cache",
  };
}

function createResponse<X>(response: Response, data: X) {
  const extendedResponse = {
    ...response,
    data,
  };

  return extendedResponse;
}
